import React, {Fragment} from 'react';
import {flatten} from 'lodash';

import {emptyArray} from '@packages/utilities/fp/utils';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {Atom, Plugins, withSystem} from '@packages/systems/wfdl';
import {excludeSearchByDefaultType} from '@packages/systems/search';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {getCommerceDataProperty} from './ContextUtils';
import {
  WF_COLLECTION_DATA_KEY,
  WF_TEMPLATE_ID_DATA_KEY,
  WF_TEMPLATE_TYPE,
} from '@packages/systems/commerce/constants';
import {px} from '@packages/systems/core/utils/WebflowCssSubset';

import type {
  AtomRenderProps,
  ChildRender,
  ContextType,
} from '@packages/systems/wfdl';

import {TListOfElement, TRecord} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment, useTemplate} from '@packages/systems/render';
import {
  getChildren,
  getInPojo,
  getString,
} from '@packages/utilities/data-utils';
import {UserItem} from './utils/OrderItemsUtils';
const {SSR, EXPORT} = WebflowEnvironments;

const getUserItems = getCommerceDataProperty('userItems', []);

const renderUserItem =
  ({
    context,
    renderChild,
    children,
  }: {
    context: ContextType;
    renderChild: ChildRender;
    children: unknown[];
  }) =>
  (item: unknown, itemIndex: number) =>
    children.map((child) =>
      renderChild(
        child,
        // TODO (ecommerce): Memoize commerce context mutations
        context.set('data', item),
        // hack; when using mockOrder, we don't get ids
        getString(getInPojo(item, ['id'])) || `orderItem-${itemIndex}`,
        itemIndex
      )
    );

const createTemplateId = (id: string) => `wf-template-${id}`;

const renderUserItems = (
  {renderChild, context, data}: AtomRenderProps,
  userItems: Array<UserItem>
) => {
  const previewItemsCount: number = context.get('previewItemsCount') || 0;
  const renderUserItemWithContext = renderUserItem({
    context,
    renderChild,
    children: getChildren(data),
  });

  return userItems.length > 0
    ? flatten(
        userItems.slice(0, previewItemsCount).map(renderUserItemWithContext)
      )
    : null;
};

const CommerceCheckoutOrderItemsList = Atom.createRender((prevProps) => {
  const {
    attributes,
    classes,
    context: prevContext,
    generateAttributes,
    elementId,
  } = prevProps;

  const userItems = getUserItems(prevContext);
  const sampleItems = prevContext.get('sampleItems', []);

  // TODO (ecommerce): Memoize commerce context mutations
  const context = prevContext.update(
    'bindingPathPrefix',
    emptyArray,
    (bindingPathPrefix) => bindingPathPrefix.concat('userItems[]')
  );

  const props = {...prevProps, context} as const;
  const attributesWithList = attributes.set('role', 'list');

  const template = useTemplate();
  const runtimeEnvironment = useRuntimeEnvironment();

  if (runtimeEnvironment === SSR || runtimeEnvironment === EXPORT) {
    const content = renderUserItems(props, sampleItems);
    const templateContent = template(content);
    const tagAttributes = {
      ...generateAttributes(attributesWithList, classes),
      [WF_COLLECTION_DATA_KEY]: 'database.commerceOrder.userItems',
      [WF_TEMPLATE_ID_DATA_KEY]: createTemplateId(elementId),
    } as const;
    return (
      <Fragment>
        <script type={WF_TEMPLATE_TYPE} id={createTemplateId(elementId)}>
          {templateContent}
        </script>
        <div {...tagAttributes}>{content}</div>
      </Fragment>
    );
  } else {
    return (
      <div {...generateAttributes(attributesWithList, classes)}>
        {renderUserItems(props, userItems)}
      </div>
    );
  }
});

const config = createIntegratedAtom({
  displayName: 'Order Item List',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Dynamo'), iconName('CollectionList')),
  },
  showEmpty: true,
  constraints: {
    ancestors: [{is: 'CommerceCheckoutOrderItemsWrapper', rule: 'ExactlyOne'}],
    wraps: ['CommerceCheckoutOrderItem'],
  },
  style: {
    base: {
      main: {
        noPseudo: {
          // Used to negate the marginBottom of last list item
          marginBottom: px(-20),
        },
      },
    },
  },
  render: CommerceCheckoutOrderItemsList,
  fields: {
    search: withSystem(
      {private: true},
      TRecord({
        exclude: excludeSearchByDefaultType,
      })
    ),
    children: TListOfElement,
  },
});

export default config;
